import { useEffect, useRef, useState } from 'react';

import type { CompareCage } from '@/domain/locationCompare';

/**
 * Tracks which cages are currently shown in the chart.
 *
 * - Defaults to all cages visible.
 * - When the cage list changes (new fetch result, different location), the
 *   selection resets to all visible — same UX as the legacy CageSelector.
 * - `toggle` is a no-op when the cage is the only one currently visible
 *   (prevents an empty chart with no series at all).
 */
export function useCageVisibility(cages: CompareCage[]) {
    const [visibleIds, setVisibleIds] = useState<Set<number>>(
        () => new Set(cages.map((c) => c.id))
    );
    const lastKeyRef = useRef('');

    useEffect(() => {
        const key = cages.map((c) => c.id).join(',');
        if (key !== lastKeyRef.current) {
            lastKeyRef.current = key;
            setVisibleIds(new Set(cages.map((c) => c.id)));
        }
    }, [cages]);

    const toggle = (cageId: number) => {
        setVisibleIds((prev) => {
            if (prev.size === 1 && prev.has(cageId)) return prev;
            const next = new Set(prev);
            if (next.has(cageId)) next.delete(cageId);
            else next.add(cageId);
            return next;
        });
    };

    return { visibleIds, toggle };
}
